import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { api } from "@/lib/api";
import { STAGE, gbp } from "@/lib/stages";
import { ukDayYear, ukDayTime } from "@/lib/time";
import { toast } from "sonner";
import {
  Badge, Btn, CellMain, CellSub, DeskSupport, Divider, Empty, Kv, LinkRow, Lname,
  Money, Note, PageBody, PageHead, Panel, RailCard, Sec, St, StatStrip, StatusChip, Tagk, Tb, Td, Th,
} from "@/components/portal/crm/kit";
import { RecruitOverrideChart } from "@/components/portal/RecruitOverrideChart";
import { RecruitCaseFeedback } from "@/components/portal/RecruitCaseFeedback";

const SITE = (process.env.REACT_APP_SITE_URL || "https://connex.is").replace(/\/+$/, "");
const MIN = 1;
/* Caps on what an introducer may set for partners they recruit from here on — mirrored by the backend. */
const CAP = { introducer: 20, lender: 10 };
const PRESET = { introducer: 10, lender: 5 };
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const STAGE_ORDER = ["new", "sent", "in_review", "offer_made"];

const range = (lo, hi) => {
  if (!hi) return null;
  return lo === hi ? gbp(hi) : `${gbp(lo)} – ${gbp(hi)}`;
};

/* Why the override is what it is — "25% of their £13k – £19k fee". The recruit's own fee is the
   projection scaled back up by the rate; a lender's "fee" is the platform spread on their live deals. */
const rateNudge = (r) => {
  const pct = Number(r.override_pct) || 0;
  if (!pct || !r.projected_max_gbp) return null;
  const k = (v) => gbp(v, { compact: true });
  const lo = (r.projected_min_gbp || 0) * 100 / pct;
  const hi = r.projected_max_gbp * 100 / pct;
  const theirs = Math.round(lo) === Math.round(hi) ? k(hi) : `${k(lo)} – ${k(hi)}`;
  return r.kind === "lender" ? `${pct}% of the ${theirs} spread on their live deals` : `${pct}% of their ${theirs} fee`;
};

const stageSummary = (stages = {}) =>
  STAGE_ORDER.filter((k) => stages[k]).map((k) => `${stages[k]} ${STAGE[k].label.toLowerCase()}`).join(" · ");

const whereItIs = (c) => {
  const n = c.desks_live || 0;
  if (c.stage === "offer_made") return "Offer on the table";
  if (c.stage === "in_review") return `${n} desk${n === 1 ? "" : "s"} reading it`;
  if (c.stage === "sent") return `With ${n} desk${n === 1 ? "" : "s"}`;
  return "Awaiting packaging";
};

const daysIn = (d) => (d === null ? null : d === 0 ? "Came in today" : `${d} day${d === 1 ? "" : "s"} in`);

const PipelineRows = ({ recruit, testId }) => {
  const rows = recruit.pipeline || [];
  if (rows.length === 0) {
    return (
      <tr className="sub" data-testid={testId}>
        <Td className="lead" colSpan={7} data-testid={`${testId}-empty`}>
          <CellSub style={{ margin: 0 }}>Nothing live right now — a case shows here the moment {recruit.name} puts one in.</CellSub>
        </Td>
      </tr>
    );
  }
  return rows.map((c, i) => {
    const st = STAGE[c.stage] || STAGE.new;
    return (
      <tr className="sub" key={i} data-testid={i === 0 ? testId : undefined}>
        <Td className="lead" data-testid={`${testId}-case-${i}`}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <StatusChip state={st} style={{ minWidth: 108 }} />
            <CellMain>{c.finance_type}</CellMain>
          </div>
        </Td>
        <Td>
          <Money raw={c.amount_band} tone="muted" />
          <CellSub>Facility size</CellSub>
        </Td>
        <Td>
          <CellMain>{ukDayYear(c.received_at, "—")}</CellMain>
          <CellSub>{daysIn(c.days_in) || "Received"}</CellSub>
        </Td>
        <Td>
          <CellMain>{whereItIs(c)}</CellMain>
          <CellSub>Desks are never named</CellSub>
        </Td>
        <Td className="r">
          <Money raw={range(c.override_min_gbp, c.override_max_gbp) || "Not priced yet"}
                 tone={c.override_max_gbp ? "ink" : "muted"} />
          <CellSub style={{ textAlign: "right" }}>If this one funds</CellSub>
        </Td>
        <Td /><Td />
      </tr>
    );
  });
};

const Slider = ({ value, onChange, testId, max }) => {
  const MAX = max;
  const pct = ((Math.min(MAX, Math.max(MIN, value)) - MIN) / (MAX - MIN)) * 100;
  return (
    <div className="slider">
      <div className="fill" style={{ width: `${pct}%` }} />
      <div className="knob" style={{ left: `calc(${pct}% - 11px)` }} />
      <div className="cap2x" style={{ left: `${pct}%` }}>{value}%</div>
      <div className="lim" style={{ left: 0 }}>{MIN}%</div>
      <div className="lim" style={{ right: 0 }}>{MAX}%</div>
      <input type="range" min={MIN} max={MAX} step={0.5} value={value} aria-label="Override rate"
             onChange={(e) => onChange(parseFloat(e.target.value))} data-testid={testId}
             style={{ position: "absolute", inset: "-9px 0 auto 0", width: "100%", height: 26, opacity: 0, cursor: "pointer" }} />
    </div>
  );
};

const LinkCard = ({ tag, title, body, url, footer, testId }) => {
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch { toast.error("Copy failed — select the link instead"); }
  };
  const invite = `mailto:?subject=${encodeURIComponent("An introduction to Connexis")}&body=${encodeURIComponent(`I work with Connexis Group on business funding. If you'd like to join, this link carries my introduction:\n\n${url}\n`)}`;
  return (
    <Panel
      testId={testId}
      footer={footer}
      style={{ marginBottom: 18 }}
    >
      <div style={{ display: "flex", gap: 28, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 300 }}>
          <Tagk>{tag}</Tagk>
          <div className="card-t" style={{ fontSize: 17, marginTop: 12 }}>{title}</div>
          <p style={{ color: "var(--mut)", fontSize: 13, margin: "8px 0 14px" }}>{body}</p>
          <div className="copyrow">
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {url.replace(/^https?:\/\//, "")}
            </span>
          </div>
          <div style={{ display: "flex", gap: 9, marginTop: 14, flexWrap: "wrap" }}>
            <Btn kind="primary" sm onClick={copy} testId={`${testId}-copy`}>Copy link</Btn>
            <Btn sm href={url} target="_blank" rel="noreferrer" testId={`${testId}-open`}>Open the page</Btn>
            <Btn sm href={invite} testId={`${testId}-email`}>Email an invitation</Btn>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <div className="rail-t">QR</div>
          <div className="qr" data-testid={`${testId}-qr`}>
            <QRCodeCanvas value={url} size={96} level="M" includeMargin={false} bgColor="#ffffff" fgColor="#0B1220" />
          </div>
        </div>
      </div>
    </Panel>
  );
};

export default function Network() {
  const nav = useNavigate();
  const [net, setNet] = useState(null);
  const [board, setBoard] = useState(null);
  const [intro, setIntro] = useState(PRESET.introducer);
  const [lender, setLender] = useState(PRESET.lender);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    try {
      const [n, b] = await Promise.all([
        api.get("/refer/network"),
        api.get("/refer/leaderboard").catch(() => ({ data: null })),
      ]);
      setNet(n.data);
      setBoard(b.data);
      setIntro(Math.min(CAP.introducer, Number(n.data.override_pct_introducer) || PRESET.introducer));
      setLender(Math.min(CAP.lender, Number(n.data.override_pct_lender) || PRESET.lender));
    } catch { setNet(null); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const recruits = useMemo(() => net?.recruits || [], [net]);
  const counts = useMemo(() => {
    const campaigns = recruits.filter((r) => r.kind !== "lender" && r.partner_kind === "campaign").length;
    const introducers = recruits.filter((r) => r.kind !== "lender").length - campaigns;
    const lenders = recruits.filter((r) => r.kind === "lender").length;
    const funded = recruits.reduce((a, r) => a + (r.funded_deals || 0), 0);
    return { introducers, lenders, campaigns, funded };
  }, [recruits]);

  const mine = (board?.entries || []).find((e) => e.is_you);
  const level2 = net?.level2_pct ?? 2.5;
  const liveCases = net?.live_cases || 0;
  const projected = range(net?.projected_min_gbp || 0, net?.projected_max_gbp || 0);
  const link = (kind) => `${SITE}/${kind}/apply?ref=${net?.referrer_id || ""}`;

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/refer/network-settings", {
        override_pct_introducer: intro, override_pct_lender: lender,
      });
      toast.success("Saved — it applies to partners you bring in from here on");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const statement = async () => {
    const now = new Date();
    const y = now.getUTCFullYear();
    const m = now.getUTCMonth() + 1;
    try {
      const res = await api.get(`/refer/statement/${y}/${m}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-statement-${y}-${String(m).padStart(2, "0")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Statement download failed — try again in a moment"); }
  };

  const copyBoth = async () => {
    try {
      await navigator.clipboard.writeText(`Introducers: ${link("introducer")}\nLenders: ${link("lender")}\nCall centres & media: ${SITE}/partner-with-us?ref=${net?.referrer_id || ""}`);
      toast.success("All three links copied");
    } catch { toast.error("Copy failed — copy them one at a time below"); }
  };

  if (loading) return <Empty testId="referrer-network-loading">Loading your network…</Empty>;

  return (
    <>
      <PageHead
        eyebrow="Super-affiliate network"
        title="Recruit partners, earn on every deal they fund"
        lede="Bring us an introducer or a lender and Connexis pays you an override on top of what they earn — funded by us, never taken out of their commission. You also earn on deals from the partners they recruit, one level deeper."
        testId="referrer-network-head"
        actions={
          <>
            <Btn onClick={statement} testId="referrer-network-statement">Download statement</Btn>
            <Btn kind="primary" onClick={copyBoth} testId="referrer-network-copy-both">Copy my links</Btn>
          </>
        }
      />

      <PageBody
        rail={
          <>
            <Panel title="How an override works" testId="referrer-network-explainer">
              <Kv rows={[
                ["Introducer you recruit", `${intro}% of their fee`],
                ["Lender you recruit", `${lender}% of our 0.5% spread`],
                ["One level deeper", `${level2}% of their fee`],
                ["Paid by", "Connexis", { style: { fontSize: 13 } }],
                ["Deducted from them", "Never", { style: { fontSize: 13 } }],
              ]} />
              <Divider style={{ margin: "18px 0" }} />
              <p style={{ color: "var(--mut)", fontSize: 12.5, margin: 0 }}>
                Overrides run for the life of the partnership, not a fixed window. If a partner you brought in is
                still funding deals in three years, you are still earning.
              </p>
            </Panel>

            <DeskSupport body="Got someone in mind? We will take the call with you and do the pitch." />

            <RailCard title="Network" testId="referrer-network-links">
              <LinkRow label={`Statement, ${MONTHS[new Date().getUTCMonth()]}`} onClick={statement} testId="referrer-network-rail-statement" />
              <LinkRow label="Recruitment wording" onClick={() => nav("/introducer/media-kit")} testId="referrer-network-rail-wording" />
            </RailCard>
          </>
        }
      >
        <StatStrip
          testId="referrer-network-stats"
          items={[
            { k: "Live in their pipeline", v: String(liveCases), h: liveCases ? (projected ? `Worth ${projected} to you if they fund` : "Not priced yet") : "No live cases from your recruits yet", testId: "network-live-cases" },
            { k: "Override owed", v: gbp(net?.override_owed_gbp), h: `From ${counts.funded} funded deal${counts.funded === 1 ? "" : "s"}`, testId: "network-override-owed" },
            { k: "Override paid", v: gbp(net?.override_paid_gbp), tone: "green", h: "Since you joined", testId: "network-override-paid" },
            { k: "Partners you brought in", v: String(recruits.length), h: `${counts.introducers} introducer${counts.introducers === 1 ? "" : "s"}, ${counts.lenders} lender${counts.lenders === 1 ? "" : "s"}${counts.campaigns ? `, ${counts.campaigns} campaign partner${counts.campaigns === 1 ? "" : "s"}` : ""}${mine ? ` · #${mine.rank} this month` : ""}`, testId: "network-partner-count" },
          ]}
        />

        <Sec
          title="Your three recruitment links"
          sub="Separate links so we know what you are introducing. All carry your code, all work from a phone, and all show the person a note saying they are joining through you."
        >
          <LinkCard
            testId="referrer-network-link-introducer"
            tag="Introducers"
            title="Recruit an introducer"
            body="Brokers, IFAs, accountants, consultants — anyone who speaks to businesses that need funding."
            url={link("introducer")}
            footer={<span>You have brought in <b style={{ color: "var(--ink-3)" }}>{counts.introducers} introducer{counts.introducers === 1 ? "" : "s"}</b> through this link.</span>}
          />
          <LinkCard
            testId="referrer-network-link-lender"
            tag="Lenders"
            title="Recruit a lender"
            body="Funders, private credit, challenger banks, family offices — anyone deploying capital who would take cases from us."
            url={link("lender")}
            footer={<span><b style={{ color: "var(--ink-3)" }}>{counts.lenders} lender{counts.lenders === 1 ? "" : "s"}</b> on the panel came from you. A lender recruit earns you an override on every deal they fund, from anybody.</span>}
          />
          <LinkCard
            testId="referrer-network-link-campaign"
            tag="Call centres & media"
            title="Recruit a campaign partner"
            body="Outbound call centres, media companies, affiliates — anyone with reach. They land on a page that names you as the introducer, and when Connexis signs them they sit in your network like any other recruit."
            url={`${SITE}/partner-with-us?ref=${net?.referrer_id || ""}`}
            footer={<span><b style={{ color: "var(--ink-3)" }}>{counts.campaigns} campaign partner{counts.campaigns === 1 ? "" : "s"}</b> came through this link. Each earns you the same override as an introducer — on every deal their reach produces.</span>}
          />
        </Sec>

        <Sec
          title="Your override rate"
          sub="You set this for partners you recruit from here on. It never changes retrospectively, and your recruit never sees it."
          right={<span className="cap" data-testid="network-rate-caps">Connexis caps: introducers {CAP.introducer}% · lenders {CAP.lender}%</span>}
        >
          <Panel
            testId="referrer-network-rates"
            footer={
              <>
                <span>Second tier is fixed at <b style={{ color: "var(--ink-3)" }}>{level2}%</b> on deals from your recruits&apos; recruits. One level deeper only.</span>
                <Btn kind="primary" sm onClick={save} disabled={saving} testId="network-save-pcts">
                  {saving ? "Saving…" : "Save rate"}
                </Btn>
              </>
            }
          >
            <div className="grid2" style={{ gap: 44 }}>
              <div>
                <div className="rail-t">On a new introducer</div>
                <Slider value={intro} onChange={setIntro} max={CAP.introducer} testId="network-pct-introducer" />
                <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "22px 0 0" }}>
                  {intro}% of whatever that introducer earns on each funded deal. If they are on 2% of a £240,000
                  facility, their fee is £4,800 and yours is <b style={{ color: "var(--ink-3)" }}>{gbp(4800 * (intro / 100))}</b>.
                </p>
              </div>
              <div>
                <div className="rail-t">On a new lender</div>
                <Slider value={lender} onChange={setLender} max={CAP.lender} testId="network-pct-lender" />
                <p style={{ color: "var(--mut)", fontSize: 12.5, margin: "22px 0 0" }}>
                  {lender}% of the platform spread on everything that desk funds — 0.5% of the facility. On a £1m deal
                  the spread is £5,000 and yours is <b style={{ color: "var(--ink-3)" }}>{gbp(5000 * (lender / 100))}</b>,
                  whoever introduced it.
                </p>
              </div>
            </div>
          </Panel>
        </Sec>

        <Sec
          title="Partners you brought in"
          sub="Live figures. Their open cases show here the moment they go in, with what each is worth to you if it funds. An override becomes owed on the day the deal funds, and is paid on your normal payout run."
        >
          <Panel pad={false} testId="referrer-network-recruits" style={{ overflow: "hidden" }}
                 footer={
                   <>
                     <span>Your recruits keep 100% of their own commission — your override is funded by Connexis and is invisible to them. You never see their applicants, their desks or their files.</span>
                     <span style={{ fontWeight: 700, color: "var(--ink-3)" }}>{ukDayTime(new Date().toISOString())} UK</span>
                   </>
                 }>
            {recruits.length === 0 ? (
              <Empty testId="network-empty">
                No partners yet. Share either link above — a recruit shows here the moment they apply.
              </Empty>
            ) : (
              <Tb testId="network-recruits-table"
                  head={<>
                    <Th>Partner</Th><Th>Type</Th><Th>Your rate</Th><Th>Live cases</Th>
                    <Th className="r">If they fund</Th><Th className="r">Owed</Th><Th className="r">Paid</Th>
                  </>}>
                {recruits.map((r) => {
                  const key = `${r.id}-${r.level}`;
                  const isOpen = open === key;
                  const live = r.live_cases || 0;
                  const proj = range(r.projected_min_gbp || 0, r.projected_max_gbp || 0);
                  return (
                    <Fragment key={key}>
                      <tr className={r.activated ? undefined : "flag-amber"} data-testid={`network-recruit-${r.id}`}>
                        <Td className="lead">
                          <CellMain>{r.name}</CellMain>
                          <CellSub>
                            {r.level === 2 ? `Tier 2 — via ${r.via || "a partner of yours"}` : "Tier 1 — recruited by you"}
                            {" · joined "}{ukDayYear(r.joined_at, "—")}
                          </CellSub>
                        </Td>
                        <Td><Lname>{r.kind === "lender" ? "Lender" : r.partner_kind === "campaign" ? "Campaign partner" : "Introducer"}</Lname></Td>
                        {r.activated ? (
                          <>
                            <Td><Money raw={`${r.override_pct}%`} /></Td>
                            <Td>
                              <button type="button" onClick={() => setOpen(isOpen ? null : key)}
                                      aria-expanded={isOpen} data-testid={`network-recruit-pipeline-${r.id}`}
                                      style={{ background: "none", border: 0, padding: 0, textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }}>
                                <CellMain>
                                  {live} live{r.funded_deals ? ` · ${r.funded_deals} funded` : ""}
                                  <span style={{ marginLeft: 6, color: "var(--mut)", display: "inline-block", transform: isOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }}>›</span>
                                </CellMain>
                                <CellSub>{live ? stageSummary(r.stages) : "Nothing live right now"}</CellSub>
                              </button>
                            </Td>
                            <Td className="r">
                              <Money raw={live ? (proj || "Not priced yet") : "—"} tone={proj ? "ink" : "muted"}
                                     testId={`network-recruit-projected-${r.id}`} />
                              {live && proj && rateNudge(r) && (
                                <CellSub style={{ textAlign: "right" }} data-testid={`network-recruit-nudge-${r.id}`}>{rateNudge(r)}</CellSub>
                              )}
                            </Td>
                            <Td className="r"><Money v={r.override_owed_gbp} tone={r.override_owed_gbp ? "ink" : "muted"} /></Td>
                            <Td className="r"><Money v={r.override_paid_gbp} tone={r.override_paid_gbp ? "green" : "muted"} /></Td>
                          </>
                        ) : (
                          <>
                            <Td><Money raw="—" tone="muted" /></Td>
                            <Td><Money raw="—" tone="muted" /></Td>
                            <Td className="r" colSpan={3}>
                              <St tone="quiet" testId={`network-recruit-pending-${r.id}`}>Awaiting approval</St>
                            </Td>
                          </>
                        )}
                      </tr>
                      {isOpen && r.activated && <PipelineRows recruit={r} testId={`network-pipeline-${r.id}`} />}
                    </Fragment>
                  );
                })}
              </Tb>
            )}
          </Panel>
        </Sec>

        <Sec
          title="How their cases are going"
          sub="The notes the desks and Connexis leave on the cases your partners bring in — so you can see they're being looked after without having to ask. Client names, contact details and documents are never shown here."
        >
          <RecruitCaseFeedback />
        </Sec>

        <Sec
          title="What each partner has earned you"
          sub="Month by month, stacked by the partner who produced it. One tall bar is one partner having a good month — and the table underneath says which."
        >
          <RecruitOverrideChart />
        </Sec>

        {!!board?.entries?.length && (
          <Sec
            title="Recruiting leaderboard"            sub={`Ranked by override earned in ${board.month} across the whole introducer network. Names are masked.`}
          >
            <Panel pad={false} testId="network-leaderboard" style={{ overflow: "hidden" }}>
              <Tb head={<><Th>Rank</Th><Th>Partner</Th><Th>Partners brought in</Th><Th className="r">Override this month</Th></>}>
                {board.entries.map((e) => (
                  <tr key={e.rank} style={e.is_you ? { background: "var(--accent-soft)" } : undefined}
                      data-testid={e.is_you ? "leaderboard-you" : `leaderboard-rank-${e.rank}`}>
                    <Td className="lead"><Money raw={String(e.rank)} size="lg" /></Td>
                    <Td>
                      <CellMain>
                        {e.name}
                        {e.is_you && <Badge style={{ marginLeft: 8 }}>You</Badge>}
                      </CellMain>
                    </Td>
                    <Td><Money raw={String(e.recruits)} tone="muted" /></Td>
                    <Td className="r"><Money v={e.earned_gbp} /></Td>
                  </tr>
                ))}
              </Tb>
            </Panel>
          </Sec>
        )}

        <Sec>
          <Note testId="referrer-network-honest">
            Overrides are paid by Connexis out of our own margin. Nothing is deducted from the partner you brought
            in, and they never see your rate. {net?.note || "Figures here move the moment a deal funds."}
          </Note>
        </Sec>
      </PageBody>
    </>
  );
}
